/** Company holidays (settings). */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { DataTable } from "@primereact/ui/datatable";
import { Select as Dropdown } from "@primereact/ui/select";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { DateInput } from "../../components/ui/controls.js";
import { Textarea } from "@primereact/ui/textarea";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { ConfirmButton, MutationError } from "../shared.js";

function HolidayManager({
  onChanged,
}: {
  onChanged: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const year = new Date().getFullYear();
  type Holiday = {
    id: string;
    date: string;
    name: string;
    paid: boolean;
    durationMinutes: number | null;
    notes: string | null;
    recurrenceType: "none" | "yearly_date" | "month_day" | "nth_weekday";
    recurrenceMonth: number | null;
    recurrenceDay: number | null;
    recurrenceWeekday: number | null;
    recurrenceOrdinal: number | null;
  };
  type Draft = Omit<Holiday, "id"> & { id?: string };
  const emptyDraft = (): Draft => ({
    date: `${year}-01-01`,
    name: "",
    paid: true,
    durationMinutes: null,
    notes: null,
    recurrenceType: "none",
    recurrenceMonth: null,
    recurrenceDay: null,
    recurrenceWeekday: null,
    recurrenceOrdinal: null,
  });
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const holidays = useQuery({
    queryKey: ["holidays", year],
    queryFn: () =>
      apiGet<{ data: Holiday[] }>("/time/holidays", {
        query: { year },
      }).then((response) => response.data),
  });
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["holidays"] });
    await onChanged();
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...draft,
        durationMinutes: draft.durationMinutes || null,
        notes: draft.notes || null,
      };
      return draft.id
        ? apiPatch(`/time/holidays/${draft.id}`, body)
        : apiPost("/time/holidays", body);
    },
    onSuccess: async () => {
      setDraft(emptyDraft());
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/time/holidays/${id}`),
    onSuccess: refresh,
  });
  const recurrenceOptions = [
    { value: "none", label: "One date" },
    { value: "yearly_date", label: "Every year on this date" },
    { value: "month_day", label: "Month and day" },
    { value: "nth_weekday", label: "Nth weekday of a month" },
  ];
  const setNumber = (key: keyof Draft, value: unknown) =>
    setDraft((current) => ({
      ...current,
      [key]: typeof value === "number" ? value : null,
    }));
  return (
    <section className="mb-5 rounded-2xl bg-app-surface p-5 shadow-sm ring-1 ring-app-border">
      <h2 className="mb-4 text-lg font-semibold text-app-text">
        {t("time.holidays", { defaultValue: "Company holidays" })}
      </h2>
      <form
        className="grid gap-3 lg:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label={t("common.date")}>
          <DateInput
            value={draft.date}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                date: value,
              }))
            }
          />
        </Field>
        <Field label={t("common.name")}>
          <Input
            required
            value={draft.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setDraft((current) => ({ ...current, name: event.target.value }))
            }
          />
        </Field>
        <Field label="Recurrence">
          <Dropdown.Root
            value={draft.recurrenceType}
            options={recurrenceOptions}
            optionLabel="label"
            optionValue="value"
            onValueChange={(event: { value: unknown }) =>
              setDraft((current) => ({
                ...current,
                recurrenceType: event.value as Draft["recurrenceType"],
              }))
            }
            fluid
          >
            <Dropdown.Trigger>
              <Dropdown.Value />
              <Dropdown.Indicator className="pi pi-chevron-down" />
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Positioner>
                <Dropdown.Popup>
                  <Dropdown.List />
                </Dropdown.Popup>
              </Dropdown.Positioner>
            </Dropdown.Portal>
          </Dropdown.Root>
        </Field>
        <Field label="Duration (minutes)">
          <InputNumber.Root
            fluid
            value={draft.durationMinutes}
            min={1}
            max={24 * 60}
            onValueChange={(event: { value: unknown }) =>
              setNumber("durationMinutes", event.value)
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
        {draft.recurrenceType === "month_day" ||
        draft.recurrenceType === "nth_weekday" ? (
          <Field label="Month">
            <InputNumber.Root
              fluid
              value={draft.recurrenceMonth}
              min={1}
              max={12}
              onValueChange={(event: { value: unknown }) =>
                setNumber("recurrenceMonth", event.value)
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
        ) : null}
        {draft.recurrenceType === "month_day" ? (
          <Field label="Day">
            <InputNumber.Root
              fluid
              value={draft.recurrenceDay}
              min={1}
              max={31}
              onValueChange={(event: { value: unknown }) =>
                setNumber("recurrenceDay", event.value)
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
        ) : null}
        {draft.recurrenceType === "nth_weekday" ? (
          <>
            <Field label="Weekday (0 = Sunday)">
              <InputNumber.Root
                fluid
                value={draft.recurrenceWeekday}
                min={0}
                max={6}
                onValueChange={(event: { value: unknown }) =>
                  setNumber("recurrenceWeekday", event.value)
                }
              >
                <InputNumber.Input as={Input} />
              </InputNumber.Root>
            </Field>
            <Field label="Occurrence">
              <InputNumber.Root
                fluid
                value={draft.recurrenceOrdinal}
                min={1}
                max={5}
                onValueChange={(event: { value: unknown }) =>
                  setNumber("recurrenceOrdinal", event.value)
                }
              >
                <InputNumber.Input as={Input} />
              </InputNumber.Root>
            </Field>
          </>
        ) : null}
        <Field label={t("common.notes")}>
          <Textarea
            rows={1}
            value={draft.notes ?? ""}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDraft((current) => ({ ...current, notes: event.target.value }))
            }
          />
        </Field>
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox.Root
              checked={draft.paid}
              onCheckedChange={(event: { checked: boolean }) =>
                setDraft((current) => ({
                  ...current,
                  paid: Boolean(event.checked),
                }))
              }
            >
              <Checkbox.Box>
                <Checkbox.Indicator match="checked">
                  <i className="pi pi-check" />
                </Checkbox.Indicator>
              </Checkbox.Box>
            </Checkbox.Root>
            Paid
          </label>
          <Button type="submit" loading={save.isPending || undefined}>
            {draft.id ? t("common.save") : t("common.add")}
          </Button>
          {draft.id ? (
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setDraft(emptyDraft())}
            >
              {t("common.cancel")}
            </Button>
          ) : null}
        </div>
      </form>
      <div className="mt-5">
        <DataTable.Root data={holidays.data ?? []} dataKey="id" size="small">
          <DataTable.TableContainer>
            <DataTable.Table>
              <DataTable.THead>
                <DataTable.THeadRow>
                  {["Date", "Name", "Recurrence", "Paid", ""].map((header) => (
                    <DataTable.THeadCell key={header}>
                      <DataTable.THeadTitle>{header}</DataTable.THeadTitle>
                    </DataTable.THeadCell>
                  ))}
                </DataTable.THeadRow>
              </DataTable.THead>
              <DataTable.TBody>
                {(context: { item: unknown; index: number }) => {
                  const holiday = context.item as Holiday;
                  return (
                    <DataTable.Row>
                      <DataTable.Cell>{holiday.date}</DataTable.Cell>
                      <DataTable.Cell>{holiday.name}</DataTable.Cell>
                      <DataTable.Cell>
                        {recurrenceOptions.find(
                          (option) => option.value === holiday.recurrenceType,
                        )?.label ?? holiday.recurrenceType}
                      </DataTable.Cell>
                      <DataTable.Cell>
                        {holiday.paid ? "Yes" : "No"}
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <div className="flex gap-2">
                          <Button
                            size="small"
                            variant="outlined"
                            severity="secondary"
                            onClick={() => setDraft({ ...holiday })}
                          >
                            {t("common.edit")}
                          </Button>
                          <ConfirmButton
                            confirmMessage="Delete this holiday?"
                            onConfirm={() => remove.mutate(holiday.id)}
                          >
                            {t("common.delete")}
                          </ConfirmButton>
                        </div>
                      </DataTable.Cell>
                    </DataTable.Row>
                  );
                }}
              </DataTable.TBody>
            </DataTable.Table>
          </DataTable.TableContainer>
        </DataTable.Root>
      </div>
      <MutationError error={save.error ?? remove.error} />
    </section>
  );
}
export function CompanyHolidaysPanel() {
  const client = useQueryClient();
  return (
    <HolidayManager
      onChanged={() =>
        client.invalidateQueries({ queryKey: ["time-management"] })
      }
    />
  );
}
